import { z } from "zod";

export const TRANSFER_STATUSES = ["READY", "PROCESSING", "UNKNOWN", "COMPLETED", "FAILED", "CANCELED"] as const;
export const TRANSFER_PURPOSES = ["TRANSACTION_PAYOUT", "CUSTOMER_REFUND"] as const;

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidTransferDate(value: string): boolean {
  if (!value) return true;
  const match = isoDatePattern.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > 9998) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export const transferSearchFormSchema = z.object({
  status: z.union([z.literal(""), z.enum(TRANSFER_STATUSES)]),
  purpose: z.union([z.literal(""), z.enum(TRANSFER_PURPOSES)]),
  accountNumber: z.string().max(30, "계좌번호는 30자 이하로 입력해 주세요.").optional(),
  keyword: z.string().max(100, "검색어는 100자 이하로 입력해 주세요."),
  memberId: z.string().refine(
    (value) => {
      const normalized = value.trim();
      return normalized === "" || (/^\d+$/.test(normalized)
        && Number.isSafeInteger(Number(normalized))
        && Number(normalized) > 0);
    },
    "회원 ID는 1 이상의 숫자로 입력해 주세요.",
  ),
  from: z.string().refine(isValidTransferDate, "유효한 시작일을 선택해 주세요."),
  to: z.string().refine(isValidTransferDate, "유효한 종료일을 선택해 주세요."),
}).superRefine((values, context) => {
  if (values.from && values.to && values.from > values.to) {
    context.addIssue({
      code: "custom",
      message: "종료일은 시작일보다 빠를 수 없습니다.",
      path: ["to"],
    });
  }
});

export type TransferSearchFormValues = z.infer<typeof transferSearchFormSchema>;

export function parseTransferStatus(value: string | null): TransferSearchFormValues["status"] {
  if (!value || value === "ALL") return "";
  const parsed = z.enum(TRANSFER_STATUSES).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parseTransferPurpose(value: string | null): TransferSearchFormValues["purpose"] {
  if (!value || value === "ALL") return "";
  const parsed = z.enum(TRANSFER_PURPOSES).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parseTransferMemberId(value: string | null): string {
  if (!value || !/^\d+$/.test(value)) return "";
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : "";
}

export function parseTransferDate(value: string | null): string {
  return value && isValidTransferDate(value) ? value : "";
}
